import React, { useCallback, useRef, useState } from "react";

import type {
  Appointment,
  AppointmentSlot,
  PatientDetails,
} from "@/features/calendar/types/schedule";
import { Button2 } from "@/ui2/atoms/Button";
import { cn } from "@/lib/helpers/cn";
import { getAutomationDataAttr } from "@/utils/automationUtils";

import { Day } from "./Day";
import {
  findFirstAvailableSlot,
  generateWeekStructure,
  getTodaysDate,
  getWeekWithFirstAvailableAppointment,
} from "./helpers";
import { Schedule } from "./Schedule";
import { TodaysDateBadge } from "./TodaysDateBadge";
import { WeekNavigation } from "./WeekNavigation";

type AppointmentTimePickerProps = {
  appointments: Appointment[];
  disableNextArrow?: boolean;
  patientDetails: PatientDetails;
  selectedAppointment: Nullable<AppointmentSlot>;
  setAppointments: React.Dispatch<React.SetStateAction<Appointment[]>>;
  setSelectedAppointment: React.Dispatch<React.SetStateAction<AppointmentSlot>>;
};

const currentWeekStartDate = getTodaysDate().startOf("isoWeek").toDate();

const DEFAULT_DAY_SIZE = 6;

export const AppointmentTimePicker = ({
  appointments,
  disableNextArrow,
  patientDetails,
  selectedAppointment,
  setAppointments,
  setSelectedAppointment,
}: AppointmentTimePickerProps) => {
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(() =>
    getWeekWithFirstAvailableAppointment(appointments, currentWeekStartDate),
  );
  const calendarBodyRef = useRef<HTMLTableSectionElement>(null);

  const handleScrollToAvailableSlotElement = useCallback((firstAvailableSlot: AppointmentSlot) => {
    if (!calendarBodyRef.current || !firstAvailableSlot) return;

    /** Find the target Day component by matching the appointment date */
    const targetDayKey = `${firstAvailableSlot.year}-${firstAvailableSlot.month}-${firstAvailableSlot.day}`;
    const targetDayElement = calendarBodyRef.current.querySelector(
      `[data-appointment-key="${targetDayKey}"]`,
    );

    if (!targetDayElement) return;

    const container = calendarBodyRef.current;
    const containerRect = container.getBoundingClientRect();
    const targetRect = targetDayElement.getBoundingClientRect();

    /** Calculate scroll positions to center the target Day
     *  Always scroll to top vertically
     *  Center horizontally
     */
    const scrollLeft =
      container.scrollLeft +
      (targetRect.left - containerRect.left) -
      (containerRect.width - targetRect.width) / 2;

    container.scrollLeft = Math.max(0, scrollLeft);
    container.scrollTop = 0;
  }, []);

  const handleEarliestAvailableClick = useCallback(() => {
    if (!appointments.length) return;

    const earliestWeekStart = getWeekWithFirstAvailableAppointment(
      appointments,
      currentWeekStartDate,
    );

    setCurrentWeekStart(earliestWeekStart);

    const firstAvailableSlot = findFirstAvailableSlot(appointments);

    if (!firstAvailableSlot) return;

    setSelectedAppointment(firstAvailableSlot);

    /** This needs to be wrapped in a timeout to ensure DOM has been updated after state updates  */
    setTimeout(() => {
      handleScrollToAvailableSlotElement(firstAvailableSlot);
    }, 0);
  }, [appointments]);

  const weekDays = generateWeekStructure(currentWeekStart, appointments);
  const daySizes = weekDays.map((day) => (day && day.slots ? day.slots.length : 0));
  const maxDaySize = Math.max(...daySizes) ?? DEFAULT_DAY_SIZE;

  return (
    <Schedule.Container
      className="tw:block tw:w-full"
      data-public
      {...getAutomationDataAttr("schedule-container")}
    >
      <Schedule.Header className="tw:flex-wrap tw:lg:flex-nowrap">
        <TodaysDateBadge />

        <div
          className={cn(
            "tw:relative tw:order-3 tw:mt-2.5 tw:flex tw:w-full tw:justify-center tw:pt-2.5",
            "tw:lg:order-none tw:lg:mt-0 tw:lg:w-auto tw:lg:pt-0",
          )}
        >
          <hr className="tw:bg-border-fill-quinary tw:absolute tw:top-0 tw:-left-3 tw:!m-0 tw:w-[calc(100%+theme(spacing.6))] tw:!p-0 tw:lg:hidden" />

          <WeekNavigation
            appointments={appointments}
            currentWeekStart={currentWeekStart}
            disableNextArrow={disableNextArrow}
            patientDetails={patientDetails}
            setAppointments={setAppointments}
            setCurrentWeekStart={setCurrentWeekStart}
          />
        </div>

        <Button2
          className="tw:order-2 tw:lg:order-none"
          onPress={handleEarliestAvailableClick}
          size="sm"
          variant="secondary"
          {...getAutomationDataAttr("appointment-time-picker-earliest")}
        >
          Earliest Available
        </Button2>
      </Schedule.Header>

      {weekDays.length > 0 && (
        <Schedule.Body
          className="tw:flex tw:h-132 tw:w-full tw:max-w-full tw:gap-1.5 tw:overflow-auto tw:lg:grid tw:lg:grid-cols-7"
          id="calendar-body"
          ref={calendarBodyRef}
        >
          {weekDays.map((appointment) => (
            <div
              data-appointment-key={`${appointment.year}-${appointment.month}-${appointment.day}`}
              key={`${appointment.month}_${appointment.day}_${appointment.year}`}
            >
              <Day
                appointment={appointment}
                maxDaySize={maxDaySize}
                selectedAppointment={selectedAppointment}
                setSelectedAppointment={setSelectedAppointment}
              />
            </div>
          ))}
        </Schedule.Body>
      )}

      {weekDays.length === 0 && (
        <div className="tw:flex tw:items-center tw:justify-center" id="calendar-body">
          <p className="tw:text-center">No sessions available in this date range.</p>
        </div>
      )}
    </Schedule.Container>
  );
};
